import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export interface AgencyMetaTokenStatus {
  hasToken: boolean;
  setAt: string | null;
  accountCount?: number;
  lastError?: string;
}

export interface AvailableMetaAdAccount {
  id: string;
  name: string;
  currency: string;
  timezone_name: string;
  assignedLocations: Array<{ id: string; name: string }>;
}

export interface AvailableMetaPage {
  id: string;
  name: string;
  category: string | null;
  instagramBusinessAccountId: string | null;
}

export interface LocationMetaStatus {
  connected: boolean;
  status?: string;
  adAccountId?: string;
  adAccountName?: string;
  pageId?: string;
  pageName?: string;
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
}

export const agencyMetaAdsApi = {
  getTokenStatus: async (): Promise<AgencyMetaTokenStatus> => {
    const { data } =
      await apiClient.get<ApiResponse<AgencyMetaTokenStatus>>('/agency/meta-ads/token');
    return data.data;
  },

  setToken: async (token: string): Promise<{ accountCount: number }> => {
    const { data } = await apiClient.put<ApiResponse<{ accountCount: number }>>(
      '/agency/meta-ads/token',
      { token },
    );
    return data.data;
  },

  clearToken: async (): Promise<void> => {
    await apiClient.delete('/agency/meta-ads/token');
  },

  getAvailableAccounts: async (): Promise<AvailableMetaAdAccount[]> => {
    const { data } = await apiClient.get<ApiResponse<AvailableMetaAdAccount[]>>(
      '/agency/meta-ads/available-accounts',
    );
    return data.data;
  },

  getAvailablePages: async (adAccountId?: string): Promise<AvailableMetaPage[]> => {
    const query = adAccountId ? `?adAccountId=${encodeURIComponent(adAccountId)}` : '';
    const { data } = await apiClient.get<ApiResponse<AvailableMetaPage[]>>(
      `/agency/meta-ads/available-pages${query}`,
    );
    return data.data;
  },

  getLocationStatus: async (locationId: string): Promise<LocationMetaStatus> => {
    const { data } = await apiClient.get<ApiResponse<LocationMetaStatus>>(
      `/agency/meta-ads/locations/${locationId}/status`,
    );
    return data.data;
  },

  assignLocation: async (
    locationId: string,
    payload: {
      adAccountId: string;
      adAccountName: string;
      pageId: string;
      pageName: string;
    },
  ): Promise<void> => {
    await apiClient.put(`/agency/meta-ads/locations/${locationId}/assignment`, payload);
  },

  unassignLocation: async (locationId: string): Promise<void> => {
    await apiClient.delete(`/agency/meta-ads/locations/${locationId}/assignment`);
  },

  syncLocation: async (locationId: string): Promise<void> => {
    await apiClient.post(`/agency/meta-ads/locations/${locationId}/sync`);
  },
};
